
import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Agent } from '@/types/database.types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Phone, Mail, MapPin } from 'lucide-react';

interface AgentListProps {
  agents: Agent[];
}

const AgentList: React.FC<AgentListProps> = ({ agents }) => {
  if (!agents.length) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-8 gap-2">
          <p className="text-muted-foreground">No agents found</p>
          <Button asChild variant="outline">
            <Link to="/agents/new">Add Agent</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Products</TableHead>
              <TableHead>Categories</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {agents.map((agent) => (
              <TableRow key={agent.id}>
                <TableCell className="font-medium">{agent.name}</TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-1">
                      <Phone className="h-3 w-3 text-muted-foreground" />
                      <span className="text-sm">{agent.phone_number}</span>
                    </div>
                    {agent.email && (
                      <div className="flex items-center gap-1">
                        <Mail className="h-3 w-3 text-muted-foreground" />
                        <span className="text-sm">{agent.email}</span>
                      </div>
                    )}
                    {agent.address && (
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3 text-muted-foreground" />
                        <span className="text-sm">{agent.address}</span>
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    <span>{agent.region}</span>
                    {agent.city && <span className="text-sm text-muted-foreground">{agent.city}</span>}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {agent.agent_products && agent.agent_products.map(product => (
                      <Badge key={product.id} variant="outline" className="text-xs">
                        {product.product_name}
                      </Badge>
                    ))}
                    {(!agent.agent_products || agent.agent_products.length === 0) && (
                      <span className="text-xs text-muted-foreground">No products</span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {agent.agent_categories && agent.agent_categories.map(category => (
                      <Badge key={category.id} variant="secondary" className="text-xs">
                        {category.category_name}
                      </Badge>
                    ))}
                    {(!agent.agent_categories || agent.agent_categories.length === 0) && (
                      <span className="text-xs text-muted-foreground">No categories</span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge 
                    variant={agent.status === 'active' ? 'success' : 'secondary'}
                    className="capitalize"
                  >
                    {agent.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button asChild size="sm" variant="outline">
                    <Link to={`/agents/${agent.id}`}>View</Link>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default AgentList;
